/**
 * ADM-040: every pharmacy order, exceptions first. Operations works this list the way support
 * works search: open a row, act there, come back.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminPharmacyOrderRow, AdminPharmacyOrders, PharmacyOrderState } from "@lattice/core";
import { isPharmacyOrderState, ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { api } from "./api.ts";
import { RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { PageHead, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href, navigate } from "./router.ts";
import { ErrorText, SelectField, Status } from "./ui.tsx";
import { day, PHARMACY_STATE, refusal } from "./words.ts";
const ALL = "";
function attention(row: AdminPharmacyOrderRow): string {
  if (row.openException !== null) return row.openException.kind.replaceAll("_", " ");
  if (row.openTask?.kind === "enter_order") return "Enter in portal";
  if (row.openTask?.kind === "cancel_order") return "Cancel in portal";
  return "None";
}
function stateTone(row: AdminPharmacyOrderRow): "ok" | "danger" | "warn" | "neutral" {
  if (row.openException !== null) return "danger";
  switch (row.state) {
    case "delivered":
      return "ok";
    case "rejected":
    case "cancelled":
    case "delivery_exception":
      return "danger";
    case "on_hold":
    case "queued":
      return "warn";
    default:
      return "neutral";
  }
}
const STATE_OPTIONS = [{
  value: ALL,
  label: "Every state"
}, ...Object.entries(PHARMACY_STATE).map(([value, label]) => ({
  value,
  label
}))];
const COLUMNS: readonly Column<AdminPharmacyOrderRow>[] = [{
  label: "Order",
  cell: row => <RecordLink to={href.pharmacyOrder(row.id)} relation="Order" label={row.orderId} />
}, {
  label: "Customer",
  cell: row => row.userId === null ? <span className="x1jty25b">
          <Truncate value={row.customerName ?? "No account"} />
        </span> : <RecordLink to={href.user(row.userId)} relation="Account" label={row.email ?? row.customerName ?? "Account"} />
}, {
  label: "Patient",
  cell: row => <RecordLink to={href.patient(row.patientId)} relation="Patient" label={row.customerName ?? "Patient record"} />
}, {
  label: "Status",
  nowrap: true,
  cell: row => <Status tone={stateTone(row)}>{PHARMACY_STATE[row.state]}</Status>
}, {
  label: "Attention",
  phoneLabel: true,
  cell: row => row.openException === null && row.openTask === null ? <span className="x1jty25b">None</span> : attention(row)
}, {
  label: "Created",
  numeric: true,
  phoneLabel: true,
  cell: row => day(row.createdAt)
}];
function List({
  result,
  state
}: {
  readonly result: AdminPharmacyOrders;
  readonly state: string;
}) {
  const exceptions = result.orders.filter(row => row.openException !== null).length;
  return <>
      <PageHead title="Pharmacy orders" meta={exceptions === 0 ? `${result.orders.length} orders` : `${result.orders.length} orders, ${exceptions} with exceptions`} />
      <div className="x78zum5 xdt5ytf x15agyyk">
        <p className="x1ghz6dp x1jty25b x15g8yj6">
          Exceptions sit at the top. Open an order to resubmit it, cancel it, or record a status the
          pharmacy told you by hand. Every change needs a reason, stored with your name.
        </p>
        <div className="x1va8c73">
          <SelectField id="order-state" label="State" value={state} options={STATE_OPTIONS} onChange={next => navigate(href.orders(next))} />
        </div>
        <Section title="Orders" count={result.orders.length}>
          <Table label="Pharmacy orders" columns={COLUMNS} rows={result.orders} rowKey={row => row.id} empty="No pharmacy orders match." />
        </Section>
      </div>
    </>;
}
export function PharmacyOrders({
  state
}: {
  readonly state: string;
}) {
  const filter: PharmacyOrderState | "" = isPharmacyOrderState(state) ? state : ALL;
  const [found] = useLoad(`pharmacy-orders-${filter}`, () => api<AdminPharmacyOrders>(ROUTES.adminPharmacyOrders, {
    query: filter === ALL ? {} : {
      state: filter
    }
  }));
  if (found.state === "loading") return <PageHead title="Pharmacy orders" meta="Loading" />;
  if (found.state === "failed") return <>
        <PageHead title="Pharmacy orders" />
        <ErrorText>{refusal(found.error)}</ErrorText>
      </>;
  return <List result={found.data} state={filter} />;
}